import JWPlayer from '@jwplayer/jwplayer-react'
import { Stack, useDidFinishSSR } from '@mythical/ui'
import { useRef, useCallback, forwardRef, useImperativeHandle } from 'react'
import * as Sentry from '@sentry/nextjs'

import { propsToPlaylistItem, VideoProps } from './props'
import EventBatcher from 'app/lib/watch-tracker'
import { VideoPlayerRef } from './useVideoPlayerControls'

const VideoPlayer = forwardRef<VideoPlayerRef, VideoProps>((props, ref) => {
  const hasMounted = useDidFinishSSR()
  const playerRef = useRef<JWPlayer>(null)
  const isMutedRef = useRef(props.muted ?? false)
  const isPlayingRef = useRef(props.autoplay ?? false)

  useImperativeHandle(
    ref,
    () => ({
      unmute: () => {
        playerRef.current?.player?.setMute(false)
        isMutedRef.current = false
      },
      mute: () => {
        playerRef.current?.player?.setMute(true)
        isMutedRef.current = true
      },
      toggleMute: () => {
        const newMuted = !isMutedRef.current
        playerRef.current?.player?.setMute(newMuted)
        isMutedRef.current = newMuted
      },
      play: () => {
        playerRef.current?.player?.play()
        isPlayingRef.current = true
      },
      pause: () => {
        playerRef.current?.player?.pause()
        isPlayingRef.current = false
      },
      togglePlay: () => {
        if (isPlayingRef.current) {
          playerRef.current?.player?.pause()
        } else {
          playerRef.current?.player?.play()
        }
        isPlayingRef.current = !isPlayingRef.current
      },
      setVolume: (volume: number) => {
        playerRef.current?.player?.setVolume(volume)
        isMutedRef.current = volume === 0
      },
      isMuted: () => isMutedRef.current,
      isPlaying: () => isPlayingRef.current,
    }),
    [],
  )

  const onPause = () => {
    isPlayingRef.current = false
    props.onVideoPause?.()
  }

  const onPlay = () => {
    isPlayingRef.current = true
    props.onVideoPlay?.()
  }

  const onTime = useCallback(
    (e) => {
      const progress = Math.round((e.position / e.duration) * 100)

      if (Math.round(e.position) % 5 === 0) {
        EventBatcher.getInstance(props.userStore).addEvent({
          content: props.contentId,
          duration: Math.round(e.duration),
          position: Math.round(e.position),
          videoProgress: progress,
        })
      }

      props.onVideoProgress?.(progress)
    },
    [props],
  )

  const onComplete = () => {
    props.onVideoEnd?.()
  }

  const onError = (e) => {
    console.error('error', e)
    Sentry.captureException(e)
  }

  const onPlayerReady = () => {
    playerRef.current?.player?.seek(props.currentProgress || 0)
  }

  const onMute = (e: { mute: boolean }) => {
    isMutedRef.current = e.mute
  }

  return (
    <Stack
      aspectRatio={16 / 9}
      bg="$bgPrimary"
      flex={1}
      borderRadius="$3"
      overflow="hidden"
    >
      {hasMounted && (
        <JWPlayer
          key={props.contentId}
          library={props.player}
          playlist={[propsToPlaylistItem(props)]}
          mute={props.muted}
          autostart={props.autoplay}
          onPlay={onPlay}
          onPause={onPause}
          onTime={onTime}
          onComplete={onComplete}
          onError={onError}
          onReady={onPlayerReady}
          onMute={onMute}
          ref={playerRef}
        />
      )}
    </Stack>
  )
})

VideoPlayer.displayName = 'VideoPlayer'

export default VideoPlayer
